/* eslint-disable no-underscore-dangle */
import axios from "axios";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import {
  isBrowser,
  createCookie,
  isCrawlerBot,
  createRootDomainCookie,
  getCookie,
  removeURLParam,
} from "@helpers/browser";
import { locales } from "../../locales";

const LIMIT_GEOLOCATION_PROMPT = 1; // days

const defaultLocale = {
  locale: "en-US",
  value: "English",
  countryName: "United States",
  country: "US", // ISO 3166 Alpha-2
  countryCode: "USA", // country code iso-3
  currency: "USD",
  currencySymbol: "$",
  iso3: "USA", // country code iso-3
  timeZone: "America/New_York",
  subdivision: "NY",
};

const convertLocaleToGEFormat = (localeData) => ({
  CountryCode: localeData.country,
  CountryCode3: localeData.iso3,
  CountryName: localeData.countryName,
  CurrencyCode: localeData.currency,
  Culture: localeData.locale,
  CurrencySymbol: localeData.currencySymbol,
  Timezone: localeData.timeZone,
  Subdivision: localeData.subdivision,
});

const defaultGeoContext = {
  clientIp: "",
  isLoadingCountry: false,
  isLoadingGE: false,
  gePriceDetails: convertLocaleToGEFormat(defaultLocale),
  countryDiffersPopupProps: null,
  coords: null,
};

export const GeoContext = createContext(defaultGeoContext);

const fixLocaleCultureCode = (geData) => {
  geData.Culture = "en-US";
  return geData;
};

const getLocaleDataFromFile = (country, subdivision) =>
  fixLocaleCultureCode(
    convertLocaleToGEFormat(
      { ...locales.find((l) => l.country === country), subdivision } || defaultLocale
    )
  );

const getCountryAtEdge = async (country) => {
  const urlGE = "/.netlify/edge-functions/geo-data-at-edge";

  const params = {};
  let returnLocale;
  try {
    const response = await axios.get(urlGE, { params });
    let geData = null;
    if (country) {
      geData = getLocaleDataFromFile(country);
    } else if (response?.data?.country !== "US") {
      geData = getLocaleDataFromFile(response?.data?.country, response?.data?.subdivision);
    }

    returnLocale = {
      ...response.data,
      geData,
    };
  } catch (error) {
    console.log("Error fetching country at edge: ", error);
    returnLocale = { ...defaultLocale, geData: convertLocaleToGEFormat(defaultLocale) };
  }
  return returnLocale;
};

const setCookieFromGEData = (geData, isCustom = false) => {
  const geCookieData = {
    countryISO: geData.CountryCode,
    currencyCode: geData.CurrencyCode,
    cultureCode: geData.Culture,
    isCustom,
  };

  // Create Cookie for Global-E
  createCookie("GlobalE_Data", encodeURIComponent(JSON.stringify(geCookieData)), 365);
  createRootDomainCookie("GlobalE_Data", encodeURIComponent(JSON.stringify(geCookieData)), 365);
};

export default function GeoProvider({ children }) {
  const [state, setState] = useState({
    ...defaultGeoContext,
    isLoadingCountry: isBrowser,
    isLoadingGE: isBrowser,
  });
  const [browserCoords, setBrowserCoords] = useState(null);
  const [countryDiffersPopupProps, setCountryDiffersPopupProps] = useState(null);
  const closeCountryDiffersPopup = useCallback(() => setCountryDiffersPopupProps(null), []);
  const updateCountry = useCallback(
    (countryCode) => {
      const customRawCookie = getCookie("GlobalE_Data");
      const { countryISO: _countryAlpha2Cookie, isCustom: _isCustomCountryCookie } =
        (customRawCookie && JSON.parse(decodeURIComponent(customRawCookie))) || {};
      /*
       ** Skip page reload if:
       ** 1. Country code is not provided
       ** 2. Country code is the same as the current one and the cookie is custom
       */
      if (
        !countryCode ||
        (countryCode === state.gePriceDetails.CountryCode &&
          _countryAlpha2Cookie === countryCode &&
          _isCustomCountryCookie)
      )
        return;
      const localeData = getLocaleDataFromFile(countryCode);
      setCookieFromGEData(localeData, true);
      removeURLParam("glCountry", null, true);
      removeURLParam("glCurrency", null, true);
      window.location.reload();
    },
    [state.gePriceDetails.CountryCode]
  );

  // Initialize user location using the browser's geolocation API
  const initUserLocation = useCallback(() => {
    if (!isBrowser || !navigator.geolocation) return;
    const fetchAndStorePosition = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setBrowserCoords(coords);
          // Send location data to Exponea
          window?.exponea?.track("user_location", {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error getting user location:", error);
          if (error.code === 1) {
            // PERMISSION_DENIED
            createCookie("_geo_location", "denied", LIMIT_GEOLOCATION_PROMPT);
          }
          // other errors are: POSITION_UNAVAILABLE and TIMEOUT => we will retry next time (so no cookie)
        },
        { timeout: 5000, maximumAge: 1000 * 60 * 5 } // timeout: 5 seconds, maximumAge: 5 minutes (we allow position of the device up to 5 minute old => faster)
      );
    };

    navigator.permissions?.query({ name: "geolocation" }).then((result) => {
      if (result.state === "granted") {
        // Permission already granted — always fetch latest position, no prompt
        fetchAndStorePosition();
      } else if (result.state === "prompt") {
        // Permission need to be asked => only ask once every LIMIT_GEOLOCATION_PROMPT days
        const geolocationCookie = getCookie("_geo_location");
        if (!geolocationCookie) {
          fetchAndStorePosition();
        }
      }
      // result.state === "denied": do nothing
    });
  }, []);

  const geoData = useMemo(() => {
    const _countryAlpha2URLParam =
      isBrowser && new URLSearchParams(window.location.search).get("glCountry");
    const customRawCookie = getCookie("GlobalE_Data");
    const { countryISO: _countryAlpha2Cookie, isCustom: _isCustomCountryCookie } =
      (customRawCookie && JSON.parse(decodeURIComponent(customRawCookie))) || {};
    const localeData =
      (_countryAlpha2URLParam && getLocaleDataFromFile(_countryAlpha2URLParam)) ||
      (customRawCookie && getLocaleDataFromFile(_countryAlpha2Cookie));
    // If GE data is still loading we return an incomplete object populated with the locale data
    const gePriceDetails =
      (state.isLoadingGE && localeData) || (!state.isLoadingGE && state.gePriceDetails) || null;
    if (gePriceDetails && gePriceDetails.CountryCode !== _countryAlpha2Cookie) {
      setCookieFromGEData(gePriceDetails);
    }

    return {
      gePriceDetails: gePriceDetails || state.gePriceDetails,
      isLoadingGE: state.isLoadingGE && localeData?.CountryCode !== "US", // We do not load GE Data for US
      isLoadingCountry: !localeData && state.isLoadingGE,
      countryDiffersPopupProps,
      closeCountryDiffersPopup,
      clientIp: state.clientIp,
      coords: browserCoords || state.coords,
      updateCountry,
      initUserLocation,

      // Only for internal GeoProvider usage
      _countryAlpha2URLParam,
      _countryAlpha2Cookie,
      _isCustomCountryCookie,
    };
  }, [
    browserCoords,
    countryDiffersPopupProps,
    closeCountryDiffersPopup,
    state.clientIp,
    state.coords,
    state.isLoadingGE,
    state.gePriceDetails,
    updateCountry,
    initUserLocation,
  ]);

  async function getGeoData() {
    /**
     * Country and IP retrieval process explanation:
     *
     * COOKIE:
     * if country is US ==> take from locale file ==> isLoadingCountry(false) ==> call backend ===> retrieve IP
     * if country RoW ===> call backend ===> call global-e ==> retrieve IP + country data
     *
     *
     * NO COOKIE:
     * call backend ===>
     *      if country is US ==> IP + retrieve 2 digit 'country' ===> take from locale file
     *      if country RoW ==> call global-e ==> retrieve IP + country data
     *
     */
    let countryLocaleData = null;
    if (geoData._countryAlpha2Cookie) {
      // COOKIE FOUND
      if (geoData._countryAlpha2Cookie === "US") {
        // US
        // (Country already set by cookie)
        const { clientIp, country, coords, userAgent } = await getCountryAtEdge();
        if (document.body) {
          document.body.dataset.windowUserAgent = window?.navigator?.userAgent;
          document.body.dataset.edgeUserAgent = userAgent;
        }
        if (
          country !== geoData._countryAlpha2Cookie &&
          !geoData._isCustomCountryCookie &&
          !geoData._countryAlpha2URLParam &&
          !isCrawlerBot()
        ) {
          // Visitor visit the website with another country than previous (not selected manually)
          setCountryDiffersPopupProps(getLocaleDataFromFile(country));
        }
        setState({ ...state, clientIp, coords });
        return;
      }
      // RoW
      const { clientIp, geData, country, coords, userAgent } = await getCountryAtEdge(
        geoData._countryAlpha2Cookie
      );
      countryLocaleData = fixLocaleCultureCode(geData);
      if (document.body) {
        document.body.dataset.windowUserAgent = window?.navigator?.userAgent;
        document.body.dataset.edgeUserAgent = userAgent;
      }
      if (
        country !== geoData._countryAlpha2Cookie &&
        !geoData._isCustomCountryCookie &&
        !geoData._countryAlpha2URLParam &&
        !isCrawlerBot()
      ) {
        setCountryDiffersPopupProps(getLocaleDataFromFile(country));
      }
      setState({
        ...state,
        gePriceDetails: countryLocaleData,
        clientIp,
        coords,
        isLoadingGE: false,
      });
      return;
    }
    // NO COOKIE
    // geData will be filled if RoW and EU *but not if US!*
    const { clientIp, geData, coords, userAgent } = await getCountryAtEdge(
      geoData._countryAlpha2URLParam
    );
    if (document.body) {
      document.body.dataset.windowUserAgent = window?.navigator?.userAgent;
      document.body.dataset.edgeUserAgent = userAgent;
    }
    if (geData && !geoData._countryAlpha2URLParam && !isCrawlerBot()) {
      setCountryDiffersPopupProps(geData);
    }
    setState({
      ...state,
      clientIp,
      coords,
      isLoadingGE: false,
      gePriceDetails: geoData._countryAlpha2URLParam && geData ? geData : state.gePriceDetails,
    });
  }

  useEffect(() => {
    getGeoData();
  }, []);

  // Update and store clientIp and country code in window to be used by blotoutwallet
  useEffect(() => {
    if (state.clientIp) {
      window.__clientIp = state.clientIp;
      window.__countryCode = state.gePriceDetails?.CountryCode || "US";
    }
  }, [state.clientIp, state.gePriceDetails?.CountryCode]);

  return <GeoContext.Provider value={geoData}>{children}</GeoContext.Provider>;
}
